import {
  Button,
  ConfirmDialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableNoData,
  TablePagination,
  usePagination,
  useSnackbarContext,
} from "@ameelio/ui";
import { useMutation } from "@apollo/client/react";
import { Stack, Tab, TableRow, Tabs } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AmeelioStaff } from "../api/graphql.ts";
import { BaseSkeleton } from "../lib/closet.tsx";
import useApolloErrorHandler from "../lib/handleApolloError.ts";
import { useIsAmeelioAdmin } from "../SessionBoundary.tsx";
import { ActivateAmeelioStaffDocument } from "./ActivateAmeelioStaff.generated.ts";
import EditAmeelioStaffModal from "./EditAmeelioStaffModal.tsx";

type AmeelioStaffPartial = Pick<
  AmeelioStaff,
  "id" | "fullName" | "email" | "entitlements" | "learnRoles" | "active"
>;

type Props = {
  staff: AmeelioStaffPartial[];
  deactivatedStaff: AmeelioStaffPartial[];
  loading: boolean;
  filters?: Record<string, unknown>;
};

function ActualStaffTable({
  records,
  onEdit,
  onActivate,
  ariaLabel,
}: {
  records: AmeelioStaffPartial[];
  onEdit?: (record: AmeelioStaffPartial) => void;
  onActivate?: (record: AmeelioStaffPartial) => void;
  ariaLabel: string;
}) {
  const { t } = useTranslation();
  const admin = useIsAmeelioAdmin();
  return (
    <Table aria-label={ariaLabel}>
      <TableHead>
        <TableRow>
          <TableCell>{t("Name")}</TableCell>
          <TableCell>{t("Email")}</TableCell>
          <TableCell>{t("Entitlements")}</TableCell>
          <TableCell>{t("Actions")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {records.map((staff) => (
          <TableRow key={staff.id}>
            <TableCell>{staff.fullName}</TableCell>
            <TableCell>{staff.email}</TableCell>
            <TableCell>{staff.entitlements.join(", ")}</TableCell>
            <TableCell>
              <Stack
                direction="row"
                sx={{
                  gap: 2,
                }}
              >
                {onEdit && (
                  <Button
                    variant="outlined"
                    disabled={!admin}
                    disabledReason={t(
                      "You do not have permission to perform this function."
                    )}
                    onClick={() => onEdit(staff)}
                  >
                    {t("Edit")}
                  </Button>
                )}
                {onActivate && (
                  <Button
                    variant="outlined"
                    disabled={!admin}
                    disabledReason={t(
                      "You do not have permission to perform this function."
                    )}
                    onClick={() => onActivate(staff)}
                  >
                    {t("Reactivate")}
                  </Button>
                )}
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function AmeelioStaffTable({
  staff,
  deactivatedStaff,
  loading,
  filters,
}: Props) {
  const { t } = useTranslation();
  const snackbarContext = useSnackbarContext();
  const handleApolloError = useApolloErrorHandler();

  const { page, rowsPerPage, currentPageData, onPageChange } = usePagination({
    data: staff,
    resetOnChange: [filters],
    pageKey: "staffPage",
  });
  const [editingStaff, setEditingStaff] = useState<AmeelioStaffPartial>();
  const [activatingStaff, setActivatingStaff] = useState<AmeelioStaffPartial>();

  const {
    currentPageData: deactivatedStaffPageData,
    page: deactivatedStaffPage,
    rowsPerPage: deactivatedStaffRowsPerPage,
    onPageChange: onDeactivatedStaffPageChange,
  } = usePagination({
    data: deactivatedStaff,
    resetOnChange: [filters],
    pageKey: "deactivatedStaffPage",
  });

  const [activeTab, setActiveTab] = useState<"active" | "deactivated">(
    "active"
  );

  const [activateStaff] = useMutation(ActivateAmeelioStaffDocument, {
    onError: handleApolloError,
    onCompleted: () => {
      snackbarContext.alert("success", t("Staff activated"));
      setActivatingStaff(undefined);
    },
  });

  if (loading) return <BaseSkeleton />;

  return (
    <>
      {editingStaff && (
        <EditAmeelioStaffModal
          onClose={() => setEditingStaff(undefined)}
          staff={editingStaff}
        />
      )}
      {activatingStaff && (
        <ConfirmDialog
          title={t("Activate staff")}
          description={t("Are you sure?", {
            name: activatingStaff?.fullName || t("this staff member"),
          })}
          variant="remove"
          onCancel={() => setActivatingStaff(undefined)}
          onConfirm={async () => {
            await activateStaff({
              variables: {
                input: {
                  id: activatingStaff.id,
                },
              },
            });
          }}
          confirmText={t("Confirm activation")}
        />
      )}
      <Stack spacing={2}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue: typeof activeTab) => setActiveTab(newValue)}
          aria-label={t("System tabs")}
          sx={{ mb: 4 }}
        >
          <Tab label={t("Active staff")} value="active" />
          <Tab label={t("Deactivated staff")} value="deactivated" />
        </Tabs>
        {activeTab === "active" && (
          <TableContainer>
            {staff.length === 0 ? (
              <Table>
                <TableNoData />
              </Table>
            ) : (
              <ActualStaffTable
                ariaLabel={t("System staff table")}
                records={currentPageData}
                onEdit={(staff) => setEditingStaff(staff)}
              />
            )}
            <TablePagination
              key="filteredStaff-pagination"
              totalCount={staff.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onPageChange}
            />
          </TableContainer>
        )}
        {activeTab === "deactivated" && (
          <TableContainer>
            {deactivatedStaff.length === 0 ? (
              <Table>
                <TableNoData />
              </Table>
            ) : (
              <ActualStaffTable
                ariaLabel={t("Deactivated system staff table")}
                records={deactivatedStaffPageData}
                onActivate={(staff) => setActivatingStaff(staff)}
              />
            )}
            <TablePagination
              key="deactivatedStaff-pagination"
              totalCount={deactivatedStaff.length}
              rowsPerPage={deactivatedStaffRowsPerPage}
              page={deactivatedStaffPage}
              onPageChange={onDeactivatedStaffPageChange}
            />
          </TableContainer>
        )}
      </Stack>
    </>
  );
}
